import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { APP_LOGIN_URL, APP_NAME } from '@app/shared/utilities/app-info';
import { AuthLayoutComponent } from '../shared/auth-layout.component';
import { Captcha, captchaMatches, createCaptcha } from '../shared/captcha';
import {
  TRIAL_ENDPOINTS,
  TrialSignupForm,
  extractApiMessage,
  loadTrialSignup,
  saveTrialSignup,
  toTrialRequest
} from '../shared/trial-signup';
import { SignupStepOneComponent } from './steps/step-one.component';
import { SignupStepTwoComponent } from './steps/step-two.component';
import { SignupStepThreeComponent } from './steps/step-three.component';

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');
  if (!password || !confirmPassword) {
    return null;
  }
  return password.value === confirmPassword.value ? null : { passwordMismatch: true };
}

/** Rejects values that are blank once trimmed (the API answers "Invalid company name"). */
function notBlankValidator(control: AbstractControl): ValidationErrors | null {
  return typeof control.value === 'string' && control.value.trim().length < 2 ? { blank: true } : null;
}

/** International phone: optional +, digits, spaces, dots, dashes, brackets. */
const PHONE_PATTERN = /^\+?[0-9][0-9\s().-]{6,19}$/;

const STEP_LABELS = ['Your details', 'Your company', 'Secure your account'];

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AuthLayoutComponent,
    SignupStepOneComponent,
    SignupStepTwoComponent,
    SignupStepThreeComponent
  ],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit {
  readonly appName = APP_NAME;
  readonly loginUrl = APP_LOGIN_URL;
  readonly sideHeading = `Start your ${APP_NAME} free trial`;
  readonly sideBullets = [
    'AI-powered shift scheduling and attendance',
    'Facial recognition and geofenced clock-ins',
    'Leave management and HR reports in one place'
  ];
  readonly stepLabels = STEP_LABELS;
  readonly totalSteps = STEP_LABELS.length;

  signupForm!: FormGroup;
  currentStep = 1;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  captcha: Captcha = createCaptcha();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiRequestService: ApiRequestService
  ) {}

  ngOnInit(): void {
    this.signupForm = this.fb.group(
      {
        // Step 1
        fullName: ['', [Validators.required, Validators.minLength(2), notBlankValidator]],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.required, Validators.pattern(PHONE_PATTERN)]],
        // Step 2
        companyName: ['', [Validators.required, Validators.minLength(2), notBlankValidator]],
        industry: ['', [Validators.required]],
        numberOfEmployees: ['', [Validators.required, Validators.min(1)]],
        // Step 3
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
        termsAccepted: [false, [Validators.requiredTrue]],
        captchaInput: ['', [Validators.required]]
      },
      { validators: passwordMatchValidator }
    );

    this.restoreIfResuming();
  }

  get f() {
    return this.signupForm.controls;
  }

  get stepLabel(): string {
    return this.stepLabels[this.currentStep - 1];
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.errorMessage = '';
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.errorMessage = '';
    }
  }

  refreshCaptcha(): void {
    this.captcha = createCaptcha();
    this.signupForm.patchValue({ captchaInput: '' });
  }

  onSubmit(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.signupForm.invalid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    if (!captchaMatches(this.captcha, this.signupForm.get('captchaInput')?.value)) {
      this.errorMessage = 'Security check answer is incorrect. Please try again.';
      this.refreshCaptcha();
      return;
    }

    this.isLoading = true;
    this.sendOtp();
  }

  /**
   * Step 1 of the API flow: the server caches the whole form for 10 minutes
   * and emails a 6-digit code. The form is kept locally so the OTP screen can
   * resend (a resend is a re-POST of the same form).
   */
  private sendOtp(): void {
    const { fullName, email, phone, companyName, industry, numberOfEmployees, password } = this.signupForm.value;
    const form: TrialSignupForm = { fullName, email, phone, companyName, industry, numberOfEmployees, password };
    const request = toTrialRequest(form);

    this.apiRequestService.postRequest<any>(TRIAL_ENDPOINTS.sendOtp, request).subscribe({
      next: (response: HttpResponse<any>) => {
        this.isLoading = false;
        if (response.status !== 200) {
          this.errorMessage = 'We could not send the verification code. Please try again.';
          this.refreshCaptcha();
          return;
        }

        saveTrialSignup(form);
        this.successMessage = response.body?.message || `A verification code has been sent to ${request.contactPersonEmail}.`;

        setTimeout(() => {
          this.router.navigate(['/auth/verify-otp'], { queryParams: { email: request.contactPersonEmail } });
        }, 1200);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = extractApiMessage(error, 'We could not send the verification code. Please try again.');
        this.refreshCaptcha();
      }
    });
  }

  /**
   * The OTP screen links back here with ?resume=details|company when the user
   * needs to change something (e.g. the company initials are already taken).
   * Re-fill the form from the saved copy so nothing has to be retyped.
   */
  private restoreIfResuming(): void {
    const resume = this.route.snapshot.queryParamMap.get('resume');
    const saved = loadTrialSignup();
    if (!resume || !saved) {
      return;
    }
    this.signupForm.patchValue({ ...saved, confirmPassword: saved.password });
    this.currentStep = resume === 'company' ? 2 : 1;
  }
}
